import { Fragment, useState } from 'react';

import { ChevronDown, ChevronRight, Copy, Pencil, Play, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateDataConnector,
  useDataConnectors,
  useDeleteDataConnector,
  useRunDataConnector,
  useTestDataConnector,
  useUpdateDataConnector,
} from '@/hooks/useDataConnectors';
import { extractErrorMessage } from '@/lib/utils';

import { ConnectorForm } from './ConnectorForm';
import { RunHistory } from './RunHistory';
import { nextRunAt } from './schedule';

import type { CreateDataConnectorDto, DataConnectorDetail } from '@repo/shared-types';

const statusVariant = (s: DataConnectorDetail['status']) =>
  s === 'error' ? 'destructive' : s === 'running' ? 'secondary' : 'outline';

const when = (iso: string | Date | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** CRM Integration → Stock → Data Connectors: configure API sources and watch their syncs. */
export const ConnectorsTab = () => {
  const { toast } = useToast();
  const { data: connectors, isLoading } = useDataConnectors();
  const create = useCreateDataConnector();
  const update = useUpdateDataConnector();
  const remove = useDeleteDataConnector();
  const run = useRunDataConnector();
  const test = useTestDataConnector();

  const [editing, setEditing] = useState<DataConnectorDetail | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DataConnectorDetail | null>(null);
  const [deleteListings, setDeleteListings] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const fail = (title: string) => (error: unknown) =>
    toast({ title, description: extractErrorMessage(error), variant: 'destructive' });

  const handleSubmit = (dto: CreateDataConnectorDto) => {
    const done = () => {
      setEditing(null);
      toast({ title: 'Connector saved' });
    };
    if (editing === 'new') {
      create.mutate(dto, { onSuccess: done, onError: fail('Could not save connector') });
    } else if (editing) {
      update.mutate(
        { id: editing.id, data: dto },
        { onSuccess: done, onError: fail('Could not save connector') },
      );
    }
  };

  const handleRun = (c: DataConnectorDetail) =>
    run.mutate(c.id, {
      onSuccess: () => {
        setExpanded(c.id);
        toast({ title: `Sync started for ${c.name}` });
      },
      onError: fail('Could not start sync'),
    });

  const handleDelete = () => {
    if (!deleting) return;
    remove.mutate(
      { id: deleting.id, deleteListings },
      {
        onSuccess: () => {
          setDeleting(null);
          toast({ title: 'Connector deleted' });
        },
        onError: fail('Could not delete connector'),
      },
    );
  };

  const copySources = async (c: DataConnectorDetail) => {
    await navigator.clipboard.writeText(`dealerkit,${c.id}`);
    toast({
      title: 'Copied',
      description:
        'Set this as the fixed value of a "sources" parameter on a stock search tool so agents also search this connector.',
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          Pull cars from any JSON API on a schedule and search them with the stock search. Agents
          only see connector cars when their stock tool passes the connector in <code>sources</code>
          .
        </p>
        <Button onClick={() => setEditing('new')}>
          <Plus className="mr-1 h-4 w-4" />
          Add connector
        </Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !connectors?.length ? (
        <p className="text-muted-foreground">No connectors yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Cars</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead>Next run</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {connectors.map((c) => {
              const open = expanded === c.id;
              const next = c.is_active ? nextRunAt(c) : null;
              return (
                <Fragment key={c.id}>
                  <TableRow>
                    <TableCell>
                      <button
                        aria-label={open ? 'Hide runs' : 'Show runs'}
                        onClick={() => setExpanded(open ? null : c.id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {open ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-muted-foreground font-mono text-xs">{c.id}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(c.status)}>
                        {c.is_active ? c.status : 'inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {c.listing_count.toLocaleString('en-GB')}
                    </TableCell>
                    <TableCell className="text-sm">
                      {when(c.last_run_at)}
                      {c.last_run_status && (
                        <span className="text-muted-foreground"> · {c.last_run_status}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{next ? when(next) : 'Manual only'}</TableCell>
                    <TableCell className="space-x-1 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRun(c)}
                        disabled={c.status === 'running' || run.isPending}
                      >
                        <Play className="mr-1 h-4 w-4" />
                        {c.status === 'running' ? 'Running…' : 'Run now'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Copy sources value for agent tools"
                        title="Copy sources value for agent tools"
                        onClick={() => copySources(c)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Edit connector"
                        onClick={() => setEditing(c)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Delete connector"
                        onClick={() => {
                          setDeleteListings(false);
                          setDeleting(c);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/30">
                        <RunHistory connectorId={c.id} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add connector' : 'Edit connector'}</DialogTitle>
            <DialogDescription>
              Describe how to call the API and where each field should be saved.
            </DialogDescription>
          </DialogHeader>
          {editing !== null && (
            <ConnectorForm
              key={editing === 'new' ? 'new' : editing.id}
              initial={editing === 'new' ? undefined : editing}
              onSubmit={handleSubmit}
              onCancel={() => setEditing(null)}
              onTest={(body) => test.mutateAsync(body)}
              isSaving={create.isPending || update.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>
              {deleting?.listing_count
                ? `This connector has ${deleting.listing_count.toLocaleString('en-GB')} cars.`
                : 'This connector has no cars.'}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={deleteListings}
              onCheckedChange={(v) => setDeleteListings(v === true)}
            />
            Also delete its cars (required when it has any)
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
